import { parseEntityRef } from '@backstage/catalog-model';

/** Returns a foreground colour with alpha that adapts to the current theme mode. */
export const fgAlpha = (mode: 'light' | 'dark', a: number) =>
  mode === 'light' ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;

/** Parses an owner ref string into a display name and navigation path. Returns null if unparseable. */
export function parseOwnerRef(
  owner: string,
): { displayName: string; path: string } | null {
  try {
    const ref = parseEntityRef(owner, {
      defaultKind: 'group',
      defaultNamespace: 'default',
    });
    return {
      displayName: ref.name,
      path: `/catalog/${ref.namespace}/${ref.kind.toLowerCase()}/${ref.name}`,
    };
  } catch {
    return null;
  }
}

export type FilterDimension =
  | 'kind'
  | 'type'
  | 'owner'
  | 'tag'
  | 'lifecycle'
  | 'domain';

export interface ActiveFilters {
  kind?: string[];
  type?: string[];
  owner?: string[];
  tag?: string[];
  lifecycle?: string[];
  domain?: string[];
  searchTerm?: string;
}

export interface FilterOptions {
  kinds: string[];
  types: string[];
  owners: string[];
  tags: string[];
  lifecycles: string[];
}

export function buildCatalogFilter(
  filters: ActiveFilters,
): Record<string, string | string[]> {
  const result: Record<string, string | string[]> = {};
  if (filters.kind?.length) result.kind = filters.kind;
  if (filters.type?.length) result['spec.type'] = filters.type;
  if (filters.owner?.length) result['spec.owner'] = filters.owner;
  if (filters.tag?.length) result['metadata.tags'] = filters.tag;
  if (filters.lifecycle?.length) result['spec.lifecycle'] = filters.lifecycle;
  if (filters.domain?.length) result['spec.domain'] = filters.domain;
  return result;
}

export function buildGetEntitiesRequest(
  filters: ActiveFilters,
  limit: number,
  offset: number,
) {
  const catalogFilter = buildCatalogFilter(filters);
  const filterValue =
    Object.keys(catalogFilter).length > 0 ? catalogFilter : undefined;

  return {
    filter: filterValue ? [filterValue] : [],
    limit,
    offset,
  };
}

export function buildQueryRequest(
  filters: ActiveFilters,
  cursor: string | undefined,
  limit: number,
) {
  const catalogFilter = buildCatalogFilter(filters);
  return {
    filter: Object.keys(catalogFilter).length > 0 ? catalogFilter : undefined,
    fullTextFilter: filters.searchTerm
      ? {
          term: filters.searchTerm,
          fields: [
            'metadata.name',
            'metadata.description',
            'spec.type',
            'spec.owner',
          ],
        }
      : undefined,
    limit,
    cursor,
    skipTotalItems: true,
  };
}
